import { useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ExpandMoreTwoToneIcon from "@mui/icons-material/ExpandMoreTwoTone";
import LockOpenTwoToneIcon from "@mui/icons-material/LockOpenTwoTone";
import {
  alpha,
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { deleteSession } from "src/slices/user";
import useNotification from "src/hooks/useNotification";
import { ChevronRightTwoTone, Logout } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import PermissionGate from "src/utils/permissionGate";

const UserBoxButton = styled(IconButton)(
  ({ theme }) => `
  width: ${theme.spacing(4)};
  padding: 0;
  height: ${theme.spacing(4)};
  margin-left: ${theme.spacing(1)};
  border-radius: ${theme.general.borderRadiusLg};
  &:hover{
          background: none ! important;
          color: none ! important;
      }
`
);

const UserAvatar = styled(Avatar)(
  ({ theme }) => `
        height: 100%;
        width: 100%;
        // border: 1px solid ${theme.colors.secondary.dark};
`
);

const MenuListWrapperPrimary = styled(MenuList)(
  ({ theme }) => `
  padding: ${theme.spacing(1)};

  & .MuiMenuItem-root {
      border-radius: 5px;
      min-width: 200px;
      margin-bottom: 2px;
      position: relative;
      color: ${theme.colors.alpha.black[100]};

      &.Mui-selected,
      &:hover,
      &.MuiButtonBase-root:active {
          background: ${theme.colors.primary.lighter};
          color: ${theme.colors.primary.main};
      }

      &:last-child {
          margin-bottom: 0;
      }
    }
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${alpha(theme.colors.alpha.black[100], 0.08)};
        padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: 400;
        color: #37352F;
        display: block;
`
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
        color: ${theme.palette.secondary.light}
`
);

function HeaderUserbox() {
  const { t }: { t: any } = useTranslation();
  const { allPermissions } = useSelector((state: any) => state.permissions);
  const theme = useTheme();
  const dispatch = useDispatch();
  const [sendNotification] = useNotification();
  const { logout } = useAuth0();
  const { users } = useSelector((state: any) => state.user);
  const sessionStorage = JSON.parse(localStorage.getItem("sessionStorage"));
  const navigate = useNavigate();
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const userDetail = JSON.parse(localStorage.getItem("userDetail"));
  const user_role = userDetail?.user_type;
  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleLogout = async (): Promise<void> => {
    try {
      handleClose();
      const sessionIds = {
        session_id: sessionStorage?._id,
      };
      let res = await dispatch<any>(deleteSession({ sessionIds: sessionIds }));
      if (res?.error) {
        sendNotification({ message: res?.payload?.message, variant: "error" });
      } else {
        logout({
          logoutParams: {
            returnTo: process.env.REACT_APP_AUTH0_LOGOUT_REDIRECT_URI,
          },
        });
        localStorage.clear();
      }
    } catch (err) {
      return err;
    }
  };

  const handleNavigation = (action: string) => {
    if (action === "TcsCorpFrofile") {
      if (user_role === "VENDOR") {
        navigate("/vendor/profile");
      } else {
        navigate("/corporate/profile");
      }

      handleClose();
    } else {
      navigate("/corporate");
      handleClose();
    }
  };

  const renderLspMenu = () => {
    switch (true) {
      case users.detail.is_primary_user:
        return (
          <>
            <MenuListWrapperPrimary disablePadding></MenuListWrapperPrimary>
            {/* <Divider /> */}
          </>
        );
      case users.detail.is_cc_user && !users.detail.is_primary_user:
        return (
          <>
            <MenuListWrapperPrimary disablePadding></MenuListWrapperPrimary>
            {/* <Divider /> */}
          </>
        );
      case !users.detail.is_primary_user && !users.detail.is_cc_user:
        return null;
      default:
        return null;
    }
  };

  return (
    <>
      <UserBoxButton color="primary" ref={ref} onClick={handleOpen}>
        <UserAvatar alt={users?.detail.first_name} src={userDetail?.vendor?.logo?.internal_name} />
        <ExpandMoreTwoToneIcon
          sx={{
            color: `${theme.colors.alpha.black[30]}`,
            opacity: 0.8,
          }}
        />
      </UserBoxButton>
      <Popover
        disableScrollLock
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuUserBox
          sx={{
            minWidth: 210,
          }}
          display="flex"
        >
          <Avatar
            variant="rounded"
            alt={users?.detail.first_name}
            src={userDetail?.vendor?.logo?.internal_name}
          />
          <UserBoxText>
            <UserBoxLabel variant="body1">
              {users?.detail.first_name + " " + users?.detail.last_name}
            </UserBoxLabel>
            <UserBoxDescription variant="body2">
              {users?.detail.email}
            </UserBoxDescription>
          </UserBoxText>
        </MenuUserBox>
        <Divider
          sx={{
            mb: 0,
          }}
        />
        {users?.detail?.role === "vendor_user" && renderLspMenu()}
        {users?.detail?.role === "vendor_admin" && renderLspMenu()}
        {/* {(users?.detail?.role === "system_admin" ||
          users?.detail?.role === "t_user") && (
          <>
          </>
        )} */}

        <MenuListWrapperPrimary disablePadding>
          <PermissionGate
            permissionType={allPermissions.types.profile}
            requiredPermissions={[allPermissions.view_profile]}
          >
            <MenuItem onClick={() => handleNavigation("TcsCorpFrofile")}>
              <ListItemText
                primaryTypographyProps={{
                  variant: "titleSmall",
                }}
                primary={t("Profile")}
              />
              {/* <ChevronRightTwoTone
                sx={{
                  color: `${theme.colors.alpha.black[30]}`,
                  opacity: 0.8,
                }}
              /> */}
            </MenuItem>
          </PermissionGate>
        </MenuListWrapperPrimary>
        <Divider />
        <Box m={0.5}>
          <Button
            sx={{
              borderRadius: "5px",
              color: "#4D515D",
              fontWeight: 400,
            }}
            // color="primary"
            fullWidth
            onClick={handleLogout}
          >
            <Logout
              sx={{
                mr: 1,
              }}
            />
            {t("Sign out")}
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderUserbox;
